import { watch } from 'vue';
import { defineStore } from 'pinia';
import log from 'loglevel';
import { OfferStatusService } from '@/api/services/OfferStatusService';
import { OfferProgress } from '@/interfaces/Offers/OfferProgress';
import { OffersProgressAndPaging } from '@/interfaces/Offers/OffersProgressAndPaging';
import { Paging } from '@/interfaces/Paging';
import { useAuthStore } from './AuthStore';

export const OfferStatusStore = defineStore('OfferStatusStore', {
    state: () => ({


        selectedOffer: {} as OfferProgress,


        offersInProgress: [] as OfferProgress[],
        offersInProgressPaging: {
            count: 24,
            offset: 0,
            page: 1,
            totalPages: 1,
            web: 1,
        } as Paging,


        completedOffers: [] as OfferProgress[],
        completedOffersPaging: {
            count: 24,
            offset: 0,
            page: 1,
            totalPages: 1,
        } as Paging,


        expiredOffers: [] as OfferProgress[],
        expiredOffersPaging: {
            count: 24,
            offset: 0,
            page: 1,
            totalPages: 1,
        } as Paging,

    }),
    actions: {
        async initialize() {
            console.log('Initializing OfferStatusStore');
            try {
                // Only fetch offers if user is logged in
                const authStore = useAuthStore();
                if (authStore.isUserLoggedIn()) {
                    await this.getAllMyOffers();
                } else {
                    log.info("User not logged in, skipping offer status initialization");
                }
                
                this.setupWatchers();
                console.log('OfferStatusStore initialized successfully');
                return true;
            } catch (error) {
                console.error('Error initializing OfferStatusStore:', error);
                return false;
            }
        },
        getAllMyOffers() {
            this.getAllMyOffersInProgress();
            this.getAllMyCompletedOffers();
            this.getAllMyExpiredOffers();            
        },
        async getAllMyOffersInProgress() {
            try {
                const response = await OfferStatusService.getAllOffersInProgress(this.offersInProgressPaging);
                this.offersInProgress = response?.data ?? [];
                this.offersInProgressPaging.totalPages = response?.paging?.totalPages ?? 1;
            } catch (error) {
                console.error('Failed to get offers in progress:', error);
            }
        },
        async getAllMyCompletedOffers() {
            try {
                const response = await OfferStatusService.getAllCompletedOffers(this.completedOffersPaging);
                this.completedOffers = response?.data ?? [];
                this.completedOffersPaging.totalPages = response?.paging?.totalPages ?? 1;
            } catch (error) {
                console.error('Failed to get completed offers:', error);
            }
        },
        async getAllMyExpiredOffers() {
            try {
                const response = await OfferStatusService.getAllExpiredOffers(this.expiredOffersPaging);
                this.expiredOffers = response?.data ?? [];
                this.expiredOffersPaging.totalPages = response?.paging?.totalPages ?? 1;
            } catch (error) {
                console.error('Failed to get expired offers:', error);
            }
        },
        setupWatchers() {
            const authStore = useAuthStore();
            
            watch(() => this.offersInProgressPaging.page, (newValue: number) => {
                if (authStore.isUserLoggedIn()) {
                    this.offersInProgressPaging.offset = (newValue - 1) * this.offersInProgressPaging.count;
                    this.getAllMyOffersInProgress();
                }
            });
            watch(() => this.completedOffersPaging.page, (newValue: number) => {
                if (authStore.isUserLoggedIn()) {
                    this.completedOffersPaging.offset = (newValue - 1) * this.completedOffersPaging.count;
                    this.getAllMyCompletedOffers();
                }
            });
            watch(() => this.expiredOffersPaging.page, (newValue: number) => {
                if (authStore.isUserLoggedIn()) {
                    this.expiredOffersPaging.offset = (newValue - 1) * this.expiredOffersPaging.count;
                    this.getAllMyExpiredOffers();
                }
            });
        },
        setOffersInProgressPage(page: number) {
            this.offersInProgressPaging.page = page;
        },
        setCompletedOffersPage(page: number) {
            this.completedOffersPaging.page = page;
        },
        setExpiredOffersPage(page: number) {
            this.expiredOffersPaging.page = page;
        },
        setSelectedOffer(offer: OfferProgress) {
            this.selectedOffer = offer;
        },
        clearStoreData() {
            this.$reset();
        },
    }
});
